import { useCallback, useMemo } from 'react';
import { useI18n } from 'next-localization';

import { useFormContext } from 'react-hook-form';

import { FormRadio } from '@/components/form/form-radio';
import { FormSelect } from '@/components/form/form-select';
import { ProviderLocation } from '@/features/profile/profile.types';
import {
  CELEBRUS_APPOINTMENT_REASON_FOR_VISIT_ID,
  CELEBRUS_APPOINTMENT_TYPE_ID,
} from '@/lib/constants';

import { AppointmentTypeOption } from '../../booking.types';
import Section from '../section';
import { NEW_PATIENT_YES_NO_OPTIONS, StepOneFormKeys } from './constants';

type AppointmentTypeProps = {
  appointmentType: string;
  appointmentTypes: AppointmentTypeOption[];
  physicianName: string;
  location: ProviderLocation;
  newPatient: string;
  onSelect: (appointmentTypeId: string) => void;
};

export const AppointmentTypeSelect = ({
  physicianName,
  appointmentType,
  appointmentTypes,
  location,
  newPatient,
  onSelect,
}: AppointmentTypeProps): JSX.Element => {
  const { t } = useI18n();

  const { setValue } = useFormContext();

  const messageForPatient = useMemo(() => {
    const isNewPatient = Boolean(parseInt(newPatient));
    const message = isNewPatient
      ? 'OAS.Booking.Appointment.NewPatientMessage'
      : 'OAS.Booking.Appointment.ExistingPatientMessage';

    return t(message, { physicianName });
  }, [newPatient, physicianName, t]);

  const resetAppointmentType = useCallback(
    () => setValue(StepOneFormKeys.appointmentType, ''),
    [setValue]
  );

  return (
    <Section title={t('OAS.Booking.Appointment.ApptType')}>
      <FormRadio
        label={t('OAS.Booking.Appointment.HaveYouSeen', { doctor: physicianName })}
        name={StepOneFormKeys.newPatient}
        radioOptions={NEW_PATIENT_YES_NO_OPTIONS}
        required
        id={CELEBRUS_APPOINTMENT_TYPE_ID}
        onChange={resetAppointmentType}
      />

      {appointmentTypes.length > 1 ? (
        <div style={{ maxWidth: 280 }}>
          {newPatient.length > 0 && (
            <FormSelect
              label={t('OAS.Booking.Appointment.WhatReasonForVisit')}
              clear={false}
              items={appointmentTypes}
              name={StepOneFormKeys.appointmentType}
              value={appointmentTypes.find((item) =>
                appointmentType ? item.value === appointmentType : item.selected
              )}
              required
              onSelectCallback={onSelect}
              id={CELEBRUS_APPOINTMENT_REASON_FOR_VISIT_ID}
              // Todo: add disabled state if HCA agrees
              // disabled={newPatient.length === 0} // disable until patient type is selected
            />
          )}
        </div>
      ) : (
        <>
          {newPatient.length > 0 && (
            <p className="neu-display-5 mt-2">
              {messageForPatient}{' '}
              <a href={`tel:${location.phone}`} aria-label={`Call ${location.phone}`}>
                {location.phone}
              </a>
              .
            </p>
          )}
        </>
      )}
    </Section>
  );
};
